import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from './Button';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  itemName: string;
  itemType: string;
  loading?: boolean;
  destructive?: boolean;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  itemType,
  loading = false,
  destructive = true
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  // Handle scroll locking and escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!loading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Background overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleOverlayClick}
      />
      
      {/* Modal panel */}
      <div 
        className="relative w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div 
          className={`transform overflow-hidden rounded-lg text-left shadow-xl transition-all ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  destructive 
                    ? 'bg-red-100 text-red-600' 
                    : 'bg-orange-100 text-orange-600'
                }`}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="ml-3">
                  <h3 className={`text-lg font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {title}
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`rounded-md p-1.5 ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                    : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                }`}
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 sm:px-6 py-4">
            <div className="text-sm">
              <p className={`mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {message}
              </p>
              
              <div className={`p-4 rounded-md border ${
                theme === 'dark' 
                  ? 'bg-gray-900 border-gray-700' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <p className={`font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {itemType}: <span className="font-semibold">{itemName}</span>
                </p>
              </div>

              {destructive && (
                <div className={`mt-4 p-3 rounded-md ${
                  theme === 'dark' 
                    ? 'bg-red-900/20 border border-red-800' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-800'
                  }`}>
                    ⚠️ This action cannot be undone. The {itemType.toLowerCase()} will be permanently deleted.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`px-4 sm:px-6 py-4 border-t flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={destructive ? "danger" : "primary"}
                onClick={onConfirm}
                disabled={loading}
                className="min-w-[100px]"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  destructive ? 'Delete Permanently' : 'Confirm'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;
